import { Box, useIsDarkMode, useTheme } from '@chronosphereio/chrono-ui';
import { BarSeriesOption, BarChart as EChartsBarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  GridComponentOption,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  TooltipComponentOption,
} from 'echarts/components';
import { ComposeOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { useChartsTheme } from '@/components';
import { EChart } from '@/components/EChart';
import { ContentDimensions, formatValue, HistogramBucket, UnitOptions } from '@/core';
import { formatBucketBoundary } from '../heat-map/heat-map-model';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  EChartsBarChart,
  GridComponent,
  DatasetComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
  MarkLineComponent,
]);

type BarChartOption = ComposeOption<BarSeriesOption | TooltipComponentOption | GridComponentOption>;

export interface DistributionChartData {
  count: number;
  value: number;
  label: string;
}

export interface DistributionChartBaseProps extends ContentDimensions {
  /**
   * The bucketed data to visualize.
   */
  buckets: HistogramBucket[];
  /**
   * The unit to use for the x-axis
   */
  unit?: UnitOptions;
  /**
   * The color to use for the primary bar
   */
  primaryColor?: string;
  /**
   * The percentiles to add markers for
   */
  percentiles: Array<{ percentile: number; value: number }>;
}

/**
 * Use the context-aware `DistributionChart.Viz` instead.
 *
 * @deprecated
 */
export function DistributionChartBase(props: DistributionChartBaseProps) {
  const { width, height, buckets, unit = { kind: 'Decimal' }, primaryColor, percentiles } = props;

  const chartsTheme = useChartsTheme();
  const { theme } = useTheme();
  const isDarkMode = useIsDarkMode();

  const [min, max] = useMemo(() => {
    let min: number | undefined = undefined;
    let max: number | undefined = undefined;

    buckets.forEach((bucket) => {
      if (min === undefined || bucket.lowerBound < min) {
        min = bucket.lowerBound;
      }
      if (max === undefined || bucket.upperBound > max) {
        max = bucket.upperBound;
      }
    });

    return [min ?? 0, max ?? 0];
  }, [buckets]);

  const xAxis = useMemo(() => {
    const xAxis: BarChartOption['xAxis'] = [
      {
        type: 'value',
        min,
        max,
        splitLine: {
          show: false,
        },
        axisLabel: {
          overflow: 'truncate',
          width: width / 3,
          formatter: (value: number) => {
            return formatValue(value, unit);
          },
        },
        boundaryGap: [0, '10%'],
        show: max !== Infinity,
        position: 'bottom',
      },
      {
        type: 'category',
        show: false,
      },
    ];

    // If the x-max is inifinity, then this is data from a time-series histogram.
    // Th are predefined buckets, and are a log2 scale bucketed histogram.
    // We need to show a second axis that include the midpoints of all those buckets
    // so that what is displayed on the x-axis makes sense
    if (max === Infinity) {
      xAxis.push({
        type: 'category',
        show: max === Infinity,
        data: [0.5, 1.5, 3, 6, 12, 24, 48, 96, 192, 384, 764, 1528, 3056, 6112, Infinity],
        position: 'bottom',
      });
    }

    return xAxis;
  }, [max, min, unit, width]);

  const option: BarChartOption = useMemo(() => {
    if (buckets == null || !buckets.length) return chartsTheme.noDataOption;

    return {
      animation: false,
      title: {
        show: false,
      },
      yAxis: {
        show: true,
        axisLabel: {
          hideOverlap: true,
          formatter: (value: number) => {
            return formatValue(value, { kind: 'Decimal', abbreviate: true });
          },
        },
      },
      xAxis,
      dataset: {
        source: [
          ['bucket', 'count'],
          ...buckets.map((bucket) => [
            `${formatBucketBoundary(bucket.lowerBound, unit)} - ${formatBucketBoundary(bucket.upperBound, unit)}`,
            bucket.count < 0 ? 0 : bucket.count,
          ]),
        ],
      },
      series: [
        {
          type: 'bar',
          barGap: 0,
          barCategoryGap: 1,
          xAxisIndex: 1,
          color: primaryColor,
          zLevel: 1,
        },
        {
          type: 'bar',
          data: [],
          markLine: {
            symbol: ['none', 'none'],
            itemStyle: {
              color: isDarkMode ? chartsTheme.thresholds.labelBackgroundColor : chartsTheme.thresholds.labelColor,
            },
            lineStyle: {
              type: 'dashed',
              width: 1,
            },
            emphasis: {
              lineStyle: {
                type: 'dashed',
                width: 1,
              },
            },
            data: (percentiles || []).map((p) => ({
              xAxis: p.value,
              name: `${p.percentile}`,
              label: {
                show: true,
                position: 'end',
                formatter: `P${p.percentile}`,
                color: chartsTheme.thresholds.labelColor,
                backgroundColor: chartsTheme.thresholds.labelBackgroundColor,
                padding: [3, 4, 1, 4],
              },
              emphasis: { disable: true },
            })),
          },
          xAxisIndex: 0,
          zLevel: 0,
        },
      ],
      tooltip: {
        appendToBody: true,
        confine: true,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: Array<{ data: [string, number] }>) => {
          if (params[0]?.data) {
            return `<b style="display: inline-block; max-width: 400px; text-wrap: wrap; word-break: break-word;">${params[0].data[0]}</b> &emsp; ${params[0].data[1] !== undefined ? formatValue(params[0].data[1], { kind: 'Decimal', abbreviate: true }) : 'No data'}`;
          }

          return 'No Data';
        },
        backgroundColor: theme.palette.background.popover,
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
        },
        borderWidth: 0,
        extraCssText: `boxShadow: ${theme.shadows[5]}`,
      },
      grid: {
        // Need to avoid the P75/90/95 values from being cut off at the top.
        top: 20,
        right: '3%',
        left: '3%',
      },
    };
  }, [
    buckets,
    chartsTheme.noDataOption,
    theme,
    unit,
    primaryColor,
    chartsTheme.thresholds.labelBackgroundColor,
    chartsTheme.thresholds.labelColor,
    percentiles,
    isDarkMode,
    xAxis,
  ]);

  return (
    <Box sx={{ width: width, height: height, overflow: 'auto' }}>
      <EChart width={width} height={height} option={option} theme={chartsTheme.echartsTheme} />
    </Box>
  );
}
